import { KeyboardEvent, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Parent, Sitter } from 'us-web-services';

import { Button, ButtonType, RatingStars } from '../../../components';

type ReviewLite = {
  ratingPercent?: number;
  relationship?: string;
  comment?: string;
};

type RelationshipWithReview = Sitter['relationship'] & { review?: ReviewLite };

type SitterWithFriend = Sitter & {
  friendRelationship?: RelationshipWithReview;
};

type Props = {
  sitter: SitterWithFriend;
  parent: Parent;
  currentUserId: number | undefined;
  setFavorite: (isFavorited: boolean, favoriteId: number, sitterId: number) => Promise<void>;
  onBook: (sitter: SitterWithFriend) => void;
  onOpenProfile: (sitter: SitterWithFriend) => void;
};

export const BookedSittersCard = ({
  sitter,
  parent,
  currentUserId,
  setFavorite,
  onBook,
  onOpenProfile,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [showReview, setShowReview] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);

  const isUser = parent.id === currentUserId;
  const profileRelationship: RelationshipWithReview | undefined = isUser
    ? sitter.relationship
    : sitter.friendRelationship;

  const reviewed = !!profileRelationship?.reviewed;
  const bookingCount = profileRelationship?.bookingCount ?? 0;
  const distance = profileRelationship?.distance;
  const city = sitter.address?.city;
  const lastAccessLabel = sitter.userStats?.lastAccessLabel;
  const isFavorited = !!sitter.relationship?.bookmarked;
  const favoriteId = sitter.relationship?.favorite?.id ?? 0;
  const blocked = !!sitter.relationship?.blocked;

  const handleCardClick = (): void => onOpenProfile(sitter);

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenProfile(sitter);
    }
  };

  const handleBookClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onBook(sitter);
  };

  const handleFavoriteClick = async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.stopPropagation();
    if (!sitter.id || favoriteBusy) return;
    setFavoriteBusy(true);
    try {
      await setFavorite(!isFavorited, favoriteId, sitter.id);
    } finally {
      setFavoriteBusy(false);
    }
  };

  const handleReviewToggle = (e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    setShowReview((prev) => !prev);
  };

  const showFriendReview = reviewed && !isUser;
  const review = profileRelationship?.review;
  const reviewerName = parent.firstName ?? '';

  return (
    <div className='mb-4'>
      <div
        role='button'
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        className={`block w-full text-left bg-white border border-neutral-200 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer ${
          showFriendReview ? 'rounded-b-none border-b-0 mb-0' : ''
        }`}
      >
        <div className='flex items-start gap-4'>
          <img
            src={sitter.pictureUrl}
            alt={sitter.name ?? ''}
            className='w-20 h-20 rounded-full object-cover flex-shrink-0'
          />
          <div className='flex-1 min-w-0'>
            <div className='flex items-start justify-between gap-2 flex-wrap'>
              <div className='flex items-center gap-2 min-w-0'>
                {isUser && !blocked && (
                  <button
                    type='button'
                    onClick={handleFavoriteClick}
                    aria-label={
                      isFavorited
                        ? t('pages.bookedSitters.card.unfavorite')
                        : t('pages.bookedSitters.card.favorite')
                    }
                    className={`hidden md:inline-flex items-center justify-center w-8 h-8 rounded-full ${
                      isFavorited ? 'text-bubblegum' : 'text-neutral-400 hover:text-bubblegum'
                    }`}
                  >
                    <FontAwesomeIcon icon={isFavorited ? faHeart : faHeartRegular} className='text-xl' />
                  </button>
                )}
                <h3 className='text-lg font-semibold truncate' id={`sitter-name-link-${sitter.id}`}>
                  {sitter.name}
                </h3>
                {!!sitter.reviewCount && (
                  <span className='inline-flex items-center'>
                    <RatingStars rating={sitter.rating} count={sitter.reviewCount} />
                  </span>
                )}
              </div>
              <Button
                buttonType={ButtonType.primary}
                id={`book-${sitter.id}`}
                className='hidden md:inline-flex justify-center text-center min-w-[88px]'
                onClick={handleBookClick}
              >
                {t('pages.bookedSitters.card.book')}
              </Button>
            </div>

            {bookingCount > 1 && (
              <div className='text-sm text-neutral-700 mt-1'>
                {isUser
                  ? t('pages.bookedSitters.card.bookedMultipleByYou')
                  : t('pages.bookedSitters.card.bookedMultipleByName', { name: parent.firstName })}
              </div>
            )}

            {lastAccessLabel && (
              <div className='text-sm text-neutral-700 mt-1 lowercase'>
                <FontAwesomeIcon icon={faBolt} className='text-amber-500 mr-1' />
                {t('pages.bookedSitters.card.activeAgo', { label: lastAccessLabel })}
              </div>
            )}

            {(city || distance) && (
              <div className='text-sm text-neutral-700 mt-1'>
                {city}
                {distance && (
                  <span className='ml-1'>
                    {t('pages.bookedSitters.card.distanceMi', { miles: distance })}
                  </span>
                )}
              </div>
            )}

            {reviewed && isUser && (
              <div className='text-sm text-neutral-600 italic mt-1'>
                {t('pages.bookedSitters.card.youReviewed')}
              </div>
            )}

            <div className='md:hidden mt-3'>
              <Button
                buttonType={ButtonType.primary}
                id={`book-mobile-${sitter.id}`}
                className='justify-center text-center'
                onClick={handleBookClick}
              >
                {t('pages.bookedSitters.card.book')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {showFriendReview && (
        <div className='border border-neutral-200 border-t-0 bg-sapphire-50 rounded-b-lg overflow-hidden'>
          {showReview && review && (
            <div className='p-4 md:p-6 flex items-start gap-3'>
              <img
                src={parent.pictureUrl}
                alt={parent.firstName ?? ''}
                className='w-14 h-14 rounded-full object-cover flex-shrink-0'
              />
              <div className='flex-1'>
                <RatingStars rating={review.ratingPercent} />
                <div className='text-sm font-semibold mt-1'>
                  {`${reviewerName} ${review.relationship ?? ''} ${sitter.firstName ?? ''}`.trim()}
                </div>
                {review.comment && (
                  <div className='text-sm text-neutral-700 mt-1 whitespace-pre-wrap'>
                    {review.comment}
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            className='text-center py-2 px-3 cursor-pointer text-sm font-semibold text-sapphire'
            onClick={handleReviewToggle}
            onKeyDown={(e): void => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                setShowReview((prev) => !prev);
              }
            }}
            role='button'
            tabIndex={0}
          >
            {showReview
              ? t('pages.bookedSitters.card.closeReview')
              : t('pages.bookedSitters.card.readReview', { name: reviewerName })}
            <FontAwesomeIcon icon={showReview ? faChevronUp : faChevronDown} className='ml-2' />
          </div>
        </div>
      )}
    </div>
  );
};
